import type { KeyboardEvent } from "react";

import { isNumber } from "./ValidationHelper";

export enum UserLanguageType {
    Undefined,
    Polish,
    English,
}

export type Prettify<T> = {
    [K in keyof T]: T[K];
} & {};

export const htmlEncode = (html: string | null | undefined): string => {
    if (!html) {
        return "";
    }

    const el = document.createElement("div");
    el.textContent = html;
    el.innerText = el.textContent;
    html = el.innerHTML;

    return html;
};

export const capitalize = (text: string) => {
    if (!text) {
        return text;
    }

    return text
        .split(" ")
        .map((word) =>
            word.length > 0 ? `${word[0].toUpperCase()}${word.substring(1).toLowerCase()}` : word,
        )
        .join(" ");
};

export const onlyNumberDot = (number: string, maxValue?: number) => {
    const num = number.replace(/[^0-9.,]/gi, "").replace(/[,]/gi, ".");
    const numLength = num.indexOf(".") < 1 ? num.length : num.indexOf(".");
    if (maxValue && maxValue < numLength) {
        return num.replace(/.$/, "");
    }

    if (num.indexOf(".") === 0 || num.split(".").length === 3) {
        return num.slice(0, num.lastIndexOf("."));
    }
    return num;
};

export const formatAmount = (cash: number, language: UserLanguageType): string => {
    const currency = "PLN";

    return language === UserLanguageType.English
        ? `${cash
              .toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              .replace(/[,]/gi, ".")} ${currency}`
        : cash.toLocaleString("pl-PL", {
              style: "currency",
              currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          });
};

export interface IAccountFormat {
    numberFormat: string;
    amount: number;
    amountFormat: string;
}

export const accountNumberFormat = (
    value: string = "",
    language: UserLanguageType = UserLanguageType.Polish,
    maxSize: number = 10,
): IAccountFormat => {
    const number = onlyNumberDot(value, maxSize);
    let amount = parseFloat(number);
    let amountFormat = "";

    if (isNumber(amount)) {
        amount = Number(amount.toFixed(2));
        amountFormat = formatAmount(amount, language);
    }

    return {
        numberFormat: number,
        amount,
        amountFormat,
    };
};

export const formatDate = (date: Date) =>
    date
        ? [date.getFullYear(), `0${date.getMonth() + 1}`.slice(-2), `0${date.getDate()}`.slice(-2)].join("-")
        : "";

export const formatBytes = (bytes: number, decimals?: number) => {
    if (bytes === 0) {
        return "0 Bytes";
    }
    const k = 1024;
    const dm = !decimals || decimals <= 0 ? 0 : decimals || 2;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const defaultToTrue = () => true;

export const onDefaultKeyboardUp = (ev: KeyboardEvent<HTMLElement>) => {
    if (ev.key === "Enter") {
        ev.currentTarget.click();
    }
};
